import React, { useMemo } from 'react'
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
} from '@mui/material'

// Helper function to format large numbers
const formatNumber = (num) => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M'
  if (num >= 1_000) return (num / 1_000).toFixed(1) + 'K'
  return num
}

// Styled Table Component
const PropertyTable = ({ title, data, dataKey, highestRankingMatch }) => (
  <Box>
    <Typography
      variant="h6"
      sx={{
        mb: 1,
        fontWeight: 500,
        textAlign: 'left',
      }}
    >
      Top Properties by {title}
    </Typography>
    <TableContainer
      component={Paper}
      sx={{
        padding: `.25rem`,
        width: `calc(100% - .75rem)`,
        textAlign: `center`,
        border: `1px solid rgba(0, 0, 0, 0.2)`,
        boxShadow: `0px 1px 0px #EBEBEB, -12px 24px 21px rgba(0, 0, 0, 0.03), 12px 24px 21px rgba(0, 0, 0, 0.03)`,
        borderRadius: `6px`,
        display: `flex`,
        flexDirection: {
          xs: `column`,
        },
      }}
    >
      <Table>
        <TableHead>
          <TableRow
            sx={{
              '& th': {
                py: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.03)',
                borderBottom: '2px solid rgba(0, 0, 0, 0.1)',
              },
            }}
          >
            <TableCell sx={{ fontWeight: 'bold', padding: '8px 16px' }}>
              #
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', padding: '8px 16px' }}>
              Type
            </TableCell>
            <TableCell
              sx={{ fontWeight: 'bold', padding: '8px 16px' }}
              align="right"
            >
              {title}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow
              key={index}
              sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                backgroundColor:
                  row.type === highestRankingMatch
                    ? 'rgba(144, 202, 249, 0.2)'
                    : 'inherit',
              }}
            >
              <TableCell sx={{ padding: '8px 16px' }}>{index + 1}</TableCell>
              <TableCell sx={{ padding: '8px 16px' }}>{row.type}</TableCell>
              <TableCell sx={{ padding: '8px 16px' }} align="right">
                {dataKey === 'avg'
                  ? `$${formatNumber(row[dataKey])}`
                  : formatNumber(row[dataKey])}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
)

const TopPropertyTables = ({ data }) => {
  // Extract property types from data
  const propertyData = Object.entries(data.details.propertyType).map(
    ([type, values]) => ({
      type,
      count: values.count,
      avg: values.avg,
    })
  )

  // Sort by count (descending) and get top 5
  const topByCount = [...propertyData]
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)

  // Sort by average price (descending) and get top 5
  const topByAvgPrice = [...propertyData]
    .sort((a, b) => b.avg - a.avg)
    .slice(0, 5)

  // Find the property type with the highest ranking that appears in both tables
  const highestRankingMatch = useMemo(() => {
    const countTypes = topByCount.map((item) => item.type)
    const avgPriceTypes = topByAvgPrice.map((item) => item.type)

    // Get matching types
    const matchingTypes = countTypes.filter((type) =>
      avgPriceTypes.includes(type)
    )

    if (matchingTypes.length === 0) {
      return null
    }

    // Find the highest ranking match in either table
    // First check if there's a match in the top position of either table
    if (matchingTypes.includes(topByCount[0]?.type)) {
      return topByCount[0].type
    }

    if (matchingTypes.includes(topByAvgPrice[0]?.type)) {
      return topByAvgPrice[0].type
    }

    // If not in top position, find the one with the best combined ranking
    let bestMatch = null
    let bestRank = Infinity

    matchingTypes.forEach((type) => {
      const countRank = topByCount.findIndex((item) => item.type === type) + 1
      const avgRank = topByAvgPrice.findIndex((item) => item.type === type) + 1
      const combinedRank = countRank + avgRank // Lower is better

      if (combinedRank < bestRank) {
        bestRank = combinedRank
        bestMatch = type
      }
    })

    return bestMatch
  }, [topByCount, topByAvgPrice])

  return (
    <Grid container spacing={3} justifyContent="center">
      <Grid item xs={12} md={6}>
        <PropertyTable
          title="Count"
          data={topByCount}
          dataKey="count"
          highestRankingMatch={highestRankingMatch}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PropertyTable
          title="Avg Price"
          data={topByAvgPrice}
          dataKey="avg"
          highestRankingMatch={highestRankingMatch}
        />
      </Grid>
    </Grid>
  )
}

export default TopPropertyTables
